/**
 * useMe — hook compartido para acceder al usuario autenticado.
 *
 * Encapsula:
 *  - Acquire token vía useAccessToken
 *  - Fetch de `GET /api/v1/me` (cacheado con TanStack Query)
 *  - Sincronización del locale del usuario con i18next (Doc 19 §2.1)
 *
 * Cualquier componente del shell (Sidebar, Topbar, TenantSwitcher,
 * UserMenu) lo consume sin duplicar el fetch — TanStack Query desduplica
 * por queryKey ['me'].
 *
 * El sync de locale se hace en un useEffect ligado al data, NO al hook
 * de t() — para evitar el loop infinito que arreglamos en B.5.a.
 */

import { useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import i18n from 'i18next'

import { fetchMe, type MeResponse } from '../api/me'
import {
  changeLocale,
  isSupportedLocale,
  type SupportedLocale,
} from '../i18n/config'
import { useAccessToken } from './useAccessToken'

export interface UseMeResult {
  me: MeResponse | undefined
  isLoading: boolean
  error: unknown
  /** True cuando el flow de auth está activo y aún no resuelto. */
  authLoading: boolean
  /** True solo si tenemos un token Y un /me cargado correctamente. */
  isAuthenticated: boolean
}

export function useMe(): UseMeResult {
  const { accessToken, loading: authLoading } = useAccessToken()

  const query = useQuery({
    queryKey: ['me', accessToken ? 'authed' : 'anon'],
    queryFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return fetchMe(accessToken)
    },
    enabled: !!accessToken,
    staleTime: 60_000,
    retry: false,
  })

  // Sincroniza `me.locale` del backend con `i18n.language` SOLO una vez
  // por sesión (per accessToken). Diseño:
  //
  //   · Login inicial → aplicar la preferencia guardada del user.
  //   · Logout/login con otra cuenta → aplicar la del nuevo user (el
  //     accessToken cambia, el ref se resetea implícitamente al detectar
  //     un token distinto).
  //   · Cambios de idioma POST-login → el switcher es la única autoridad.
  //     NO re-sincronizar desde `me.locale`, porque refetches de /me
  //     (disparados por `invalidateQueries()` del listener global o por
  //     foco/staleTime) llegan con valores stale relativo al switcher
  //     in-flight, y compararlos contra `i18n.language` produce reverts
  //     visibles ("flash al segundo cambio de idioma").
  //
  // Caso fuera de scope intencional: si el user cambia su preferencia en
  // otro dispositivo, esta sesión NO la aplica automáticamente — se
  // refleja en el próximo reload. Patrón estándar en SaaS (Notion, Linear,
  // GitHub) y consistente con la idea de "el switcher local es autoridad".
  const lastSyncedAccessToken = useRef<string | null>(null)
  useEffect(() => {
    if (!accessToken) return
    const locale = query.data?.locale
    if (!locale || !isSupportedLocale(locale)) return
    if (lastSyncedAccessToken.current === accessToken) return
    lastSyncedAccessToken.current = accessToken
    if (locale !== i18n.language) {
      void changeLocale(locale as SupportedLocale)
    }
  }, [query.data?.locale, accessToken])

  return {
    me: query.data,
    isLoading: query.isLoading,
    error: query.error,
    authLoading,
    isAuthenticated: !!accessToken && !!query.data,
  }
}
